// @ts-nocheck
/**
 * ローソク足チャートコンポーネント
 *
 * OHLCデータをローソク足形式で表示
 *
 * 特徴:
 * - SVGベースで軽量
 * - レスポンシブ対応
 * - タッチ/ホバーでデータポイント詳細表示
 * - 粒度に応じたX軸ラベル自動調整
 */

import React, { useState, useMemo, useCallback } from 'react';
import type { OHLCData, PeriodType } from '../hooks/useCardPriceHistory';

// ========================================
// 型定義
// ========================================

interface CandlestickChartProps {
  /** OHLCデータ配列 */
  data: OHLCData[];
  /** チャート幅 */
  width?: number;
  /** チャート高さ */
  height?: number;
  /** 期間タイプ（X軸ラベル形式に影響） */
  period: PeriodType;
  /** 陽線の色 */
  bullColor?: string;
  /** 陰線の色 */
  bearColor?: string;
  /** コンパクトモード（ラベルなし） */
  compact?: boolean;
}

interface TooltipData {
  x: number;
  y: number;
  data: OHLCData;
  index: number;
}

// ========================================
// スタイル定義
// ========================================

const styles = {
  container: {
    position: 'relative' as const,
    width: '100%',
  },
  svg: {
    display: 'block',
    width: '100%',
    height: 'auto',
  },
  tooltip: {
    position: 'absolute' as const,
    background: 'rgba(20, 20, 40, 0.95)',
    border: '1px solid rgba(107, 76, 230, 0.5)',
    borderRadius: '8px',
    padding: '8px 12px',
    fontSize: '12px',
    color: '#fff',
    pointerEvents: 'none' as const,
    zIndex: 10,
    whiteSpace: 'nowrap' as const,
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
  },
  tooltipRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '16px',
    marginBottom: '2px',
  },
  tooltipLabel: {
    color: '#a0a0a0',
  },
  tooltipValue: {
    fontWeight: 'bold' as const,
  },
};

// ========================================
// ユーティリティ関数
// ========================================

/**
 * 日時をフォーマット
 */
function formatDateTime(date: Date, period: PeriodType): string {
  const d = new Date(date);

  switch (period) {
    case '24h':
      return d.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
    case '7d':
      return d.toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' });
    case '30d':
    case '90d':
      return d.toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' });
    default:
      return d.toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' });
  }
}

/**
 * X軸ラベルの間隔を計算
 */
function calculateLabelInterval(dataLength: number, period: PeriodType): number {
  switch (period) {
    case '24h':
      // 24時間: 4時間ごと（96データ点なら16間隔）
      return Math.max(1, Math.floor(dataLength / 6));
    case '7d':
      // 7日: 1日ごと
      return Math.max(1, Math.floor(dataLength / 7));
    case '30d':
      // 30日: 5日ごと
      return Math.max(1, Math.floor(dataLength / 6));
    case '90d':
      // 90日: 2週間ごと
      return Math.max(1, Math.floor(dataLength / 6));
    default:
      return Math.max(1, Math.floor(dataLength / 6));
  }
}

// ========================================
// メインコンポーネント
// ========================================

export function CandlestickChart({
  data,
  width = 300,
  height = 150,
  period,
  bullColor = '#4ade80',
  bearColor = '#f87171',
  compact = false,
}: CandlestickChartProps) {
  const [tooltip, setTooltip] = useState<TooltipData | null>(null);

  // チャートパディング
  const padding = useMemo(() => ({
    top: 10,
    right: 10,
    bottom: compact ? 5 : 25,
    left: compact ? 5 : 45,
  }), [compact]);

  // チャート描画領域
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  // 価格の範囲を計算
  const { minPrice, maxPrice, priceRange } = useMemo(() => {
    if (data.length === 0) {
      return { minPrice: 0, maxPrice: 100, priceRange: 100 };
    }

    const allPrices = data.flatMap(d => [d.high, d.low]);
    const min = Math.min(...allPrices);
    const max = Math.max(...allPrices);
    const range = max - min || 1;

    // 上下に5%のマージン
    const margin = range * 0.05;
    return {
      minPrice: min - margin,
      maxPrice: max + margin,
      priceRange: range + margin * 2,
    };
  }, [data]);

  // 価格→Y座標変換
  const priceToY = useCallback((price: number) => {
    return padding.top + chartHeight - ((price - minPrice) / priceRange) * chartHeight;
  }, [padding.top, chartHeight, minPrice, priceRange]);

  // ローソク足の幅と間隔
  const candleWidth = useMemo(() => {
    if (data.length === 0) return 0;
    const totalWidth = chartWidth;
    const gap = 2;
    return Math.max(2, (totalWidth - gap * (data.length - 1)) / data.length);
  }, [data.length, chartWidth]);

  // インデックス→X座標変換
  const indexToX = useCallback((index: number) => {
    const gap = 2;
    return padding.left + index * (candleWidth + gap) + candleWidth / 2;
  }, [padding.left, candleWidth]);

  // マウスイベントハンドラ
  const handleMouseMove = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    if (data.length === 0) return;

    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // 最も近いデータポイントを探す
    const scaledX = (x / rect.width) * width;
    const gap = 2;
    const index = Math.round((scaledX - padding.left - candleWidth / 2) / (candleWidth + gap));

    if (index >= 0 && index < data.length) {
      setTooltip({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
        data: data[index],
        index,
      });
    }
  }, [data, width, padding.left, candleWidth]);

  const handleMouseLeave = useCallback(() => {
    setTooltip(null);
  }, []);

  // Y軸ラベル
  const yAxisLabels = useMemo(() => {
    if (compact) return [];

    const labels: { value: number; y: number }[] = [];
    const step = priceRange / 4;

    for (let i = 0; i <= 4; i++) {
      const value = minPrice + step * i;
      labels.push({
        value: Math.round(value),
        y: priceToY(value),
      });
    }

    return labels;
  }, [compact, minPrice, priceRange, priceToY]);

  // X軸ラベル
  const xAxisLabels = useMemo(() => {
    if (compact || data.length === 0) return [];

    const interval = calculateLabelInterval(data.length, period);
    const labels: { text: string; x: number }[] = [];

    for (let i = 0; i < data.length; i += interval) {
      labels.push({
        text: formatDateTime(data[i].period, period),
        x: indexToX(i),
      });
    }

    return labels;
  }, [compact, data, period, indexToX]);

  if (data.length === 0) {
    return (
      <div style={{
        ...styles.container,
        height: `${height}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#808080',
        fontSize: '14px',
      }}>
        データがありません
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        style={styles.svg}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {/* 背景グリッド */}
        <defs>
          <pattern
            id="grid"
            width={chartWidth / 6}
            height={chartHeight / 4}
            patternUnits="userSpaceOnUse"
            x={padding.left}
            y={padding.top}
          >
            <path
              d={`M ${chartWidth / 6} 0 L 0 0 0 ${chartHeight / 4}`}
              fill="none"
              stroke="rgba(255,255,255,0.05)"
              strokeWidth="1"
            />
          </pattern>
        </defs>
        <rect
          x={padding.left}
          y={padding.top}
          width={chartWidth}
          height={chartHeight}
          fill="url(#grid)"
        />

        {/* Y軸ラベル */}
        {yAxisLabels.map((label, i) => (
          <g key={`y-${i}`}>
            <line
              x1={padding.left}
              y1={label.y}
              x2={padding.left + chartWidth}
              y2={label.y}
              stroke="rgba(255,255,255,0.1)"
              strokeWidth="1"
            />
            <text
              x={padding.left - 5}
              y={label.y + 4}
              textAnchor="end"
              fill="#808080"
              fontSize="10"
            >
              {label.value}
            </text>
          </g>
        ))}

        {/* X軸ラベル */}
        {xAxisLabels.map((label, i) => (
          <text
            key={`x-${i}`}
            x={label.x}
            y={height - 5}
            textAnchor="middle"
            fill="#808080"
            fontSize="10"
          >
            {label.text}
          </text>
        ))}

        {/* ローソク足 */}
        {data.map((candle, i) => {
          const x = indexToX(i);
          const isBull = candle.close >= candle.open;
          const color = isBull ? bullColor : bearColor;

          const bodyTop = priceToY(Math.max(candle.open, candle.close));
          const bodyBottom = priceToY(Math.min(candle.open, candle.close));
          const bodyHeight = Math.max(1, bodyBottom - bodyTop);

          const wickTop = priceToY(candle.high);
          const wickBottom = priceToY(candle.low);

          return (
            <g key={i}>
              {/* ヒゲ（上下の線） */}
              <line
                x1={x}
                y1={wickTop}
                x2={x}
                y2={wickBottom}
                stroke={color}
                strokeWidth="1"
              />
              {/* 実体（四角形） */}
              <rect
                x={x - candleWidth / 2}
                y={bodyTop}
                width={candleWidth}
                height={bodyHeight}
                fill={isBull ? color : color}
                stroke={color}
                strokeWidth="1"
                opacity={isBull ? 0.8 : 1}
              />
            </g>
          );
        })}

        {/* ホバーハイライト */}
        {tooltip && (
          <rect
            x={indexToX(tooltip.index) - candleWidth / 2 - 2}
            y={padding.top}
            width={candleWidth + 4}
            height={chartHeight}
            fill="rgba(107, 76, 230, 0.2)"
            stroke="rgba(107, 76, 230, 0.5)"
            strokeWidth="1"
          />
        )}
      </svg>

      {/* ツールチップ */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: Math.min(tooltip.x + 10, width - 150),
            top: Math.max(tooltip.y - 80, 0),
          }}
        >
          <div style={{ marginBottom: '4px', color: '#a0a0ff', fontWeight: 'bold' }}>
            {formatDateTime(tooltip.data.period, period)}
          </div>
          <div style={styles.tooltipRow}>
            <span style={styles.tooltipLabel}>始値:</span>
            <span style={styles.tooltipValue}>{tooltip.data.open.toLocaleString()}G</span>
          </div>
          <div style={styles.tooltipRow}>
            <span style={styles.tooltipLabel}>高値:</span>
            <span style={{ ...styles.tooltipValue, color: '#4ade80' }}>
              {tooltip.data.high.toLocaleString()}G
            </span>
          </div>
          <div style={styles.tooltipRow}>
            <span style={styles.tooltipLabel}>安値:</span>
            <span style={{ ...styles.tooltipValue, color: '#f87171' }}>
              {tooltip.data.low.toLocaleString()}G
            </span>
          </div>
          <div style={styles.tooltipRow}>
            <span style={styles.tooltipLabel}>終値:</span>
            <span style={styles.tooltipValue}>{tooltip.data.close.toLocaleString()}G</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default CandlestickChart;
